import { Charge } from '@app/domain/Charge'
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { DurationUnit } from '../../pipes/duration.pipe'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { startWith, takeUntil } from 'rxjs'
import DineroFactory, { Currency } from 'dinero.js'
import { NumericValueType, RxwebValidators } from '@rxweb/reactive-form-validators'

@Component({
    selector: 'app-edit-charge-form',
    templateUrl: './edit-charge-form.component.html',
})
export class EditChargeFormComponent implements OnInit, OnDestroy {

    @Input() chargeType!: 'cancellationCharge' | 'depositCharge'
    @Input() partySize!: number
    @Input() charge!: Charge | null
    @Input() previousExpiryDate!: Date | null
    @Input() expiryMinutes!: number | null
    @Input() showExpiryTime: boolean = true
    @Input() currencyCode: string = 'GBP'
    @Input() amountOnly = false
    @Output() chargeChange = new EventEmitter<Charge | null>()
    @Output() expiryMinutesChange = new EventEmitter<number | null>()
    invalid = false
    form!: FormGroup
    addPaymentExpiryOptions = [
        null,
        1 * 60,
        2 * 60,
        3 * 60,
        4 * 60,
        5 * 60,
        6 * 60,
        12 * 60,
        1 * 24 * 60,
        2 * 24 * 60,
        3 * 24 * 60,
        4 * 24 * 60,
        5 * 24 * 60,
        6 * 24 * 60,
        7 * 24 * 60,
    ]
    private fb = inject(FormBuilder)
    private onDestroy$ = new EventEmitter<void>()
    protected readonly DurationUnit = DurationUnit

    ngOnInit() {
        let amount: number | null = null
        if (this.charge !== null) {
            const money = DineroFactory({
                amount: this.charge.unitAmount,
                currency: this.currencyCode as Currency,
            })
            amount = money.toRoundedUnit(2)
        }

        const isCustomExpiry = this.expiryMinutes !== null &&
            this.expiryMinutes !== -1 &&
            !this.addPaymentExpiryOptions.includes(this.expiryMinutes)

        const { value, unit } = isCustomExpiry ? this.convertMinutesToLargestUnit(this.expiryMinutes!) : { value: 1, unit: 'days' }

        this.form = this.fb.group({
            amount: [
                amount,
                this.chargeType === 'cancellationCharge' ? [
                    Validators.required,
                    Validators.min(0.01),
                    RxwebValidators.numeric({
                        allowDecimal: true,
                        acceptValue: NumericValueType.PositiveNumber,
                    }),
                ] : [
                    Validators.required,
                    Validators.min(0.01),
                    RxwebValidators.numeric({
                        allowDecimal: true,
                        acceptValue: NumericValueType.PositiveNumber,
                    }),
                ]
            ],
            partySize: [this.partySize],
            isPerCover: [this.amountOnly ? false : this.charge?.isPerCover ?? true],
            expiryMinutes: [isCustomExpiry ? 'custom' : this.expiryMinutes],
            isCustomExpiry: [isCustomExpiry],
            customExpiryValue: [value],
            customExpiryUnit: [unit],
        })

        if (isCustomExpiry) {
            this.form.patchValue({ expiryMinutes: this.expiryMinutes }, { emitEvent: false })
        }

        this.bindFormToCharge()
        this.bindExpirySelectToExpiryMinutes()
        this.form.valueChanges
            .pipe(
                startWith(this.form.value),
                takeUntil(this.onDestroy$)
            )
            .subscribe(() => {
                this.invalid = this.form.invalid
            })
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    cancelCustomExpiry() {
        this.form.patchValue({
            isCustomExpiry: false,
            customExpiryValue: 1,
            customExpiryUnit: 'days',
            expiryMinutes: this.addPaymentExpiryOptions[0]
        })
    }

    private bindExpirySelectToExpiryMinutes() {
        this.form.get('expiryMinutes')?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(value => {
                if (value === 'custom') {
                    this.form.patchValue({
                        isCustomExpiry: true,
                        customExpiryValue: 1,
                        customExpiryUnit: 'days',
                        expiryMinutes: this.calculateCustomExpiryMinutes()
                    })
                }
            })
        this.form.get('customExpiryValue')?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                if (this.form.get('isCustomExpiry')?.value) {
                    this.form.patchValue({
                        expiryMinutes: this.calculateCustomExpiryMinutes()
                    }, { emitEvent: false })
                }
            })
        this.form.get('customExpiryUnit')?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                if (this.form.get('isCustomExpiry')?.value) {
                    this.form.patchValue({
                        expiryMinutes: this.calculateCustomExpiryMinutes()
                    }, { emitEvent: false })
                }
            })
    }

    private calculateCustomExpiryMinutes(): number {
        const value = this.form.get('customExpiryValue')?.value || 1
        const unit = this.form.get('customExpiryUnit')?.value || 'days'
        switch (unit) {
            case 'hours':
                return value * 60
            case 'days':
                return value * 24 * 60
            case 'weeks':
                return value * 7 * 24 * 60
            default:
                return value * 60
        }
    }

    private bindFormToCharge() {
        this.form.valueChanges
            .pipe(
                takeUntil(this.onDestroy$)
            )
            .subscribe(() => {
                if (this.form.invalid) {
                    this.charge = null
                    this.chargeChange.emit(this.charge)
                    return
                }
                const amount = this.form.get('amount')?.value
                if (amount === null) {
                    this.charge = null
                    this.chargeChange.emit(this.charge)
                    return
                }
                const unitAmount = Math.round(amount * 100)
                const isPerCover = this.form.get('isPerCover')?.value
                this.charge = new Charge(
                    this.form.get('partySize')?.value,
                    null,
                    null,
                    unitAmount,
                    isPerCover
                )
                this.chargeChange.emit(this.charge)
                let expiryMinutes = this.form.get('expiryMinutes')?.value
                this.expiryMinutes = expiryMinutes     
                this.expiryMinutesChange.emit(this.expiryMinutes)
            })
    }

    private convertMinutesToLargestUnit(minutes: number): { value: number, unit: string } {
        if (minutes % (7 * 24 * 60) === 0) {
            return { value: minutes / (7 * 24 * 60), unit: 'weeks' }
        }
        if (minutes % (24 * 60) === 0) {
            return { value: minutes / (24 * 60), unit: 'days' }
        }
        return { value: minutes / 60, unit: 'hours' }
    }
}
